import { z } from 'zod';

/**
 * Esquemas de las tres formas de la fase 1.
 *
 * Los patrones del documento son los MISMOS de `usuario_documento_fmt_ck` en la base,
 * y la regla de la sede es la misma de `usuario_sede_ck`. La base sigue siendo la
 * autoridad: esto existe para que el error salga antes de la llamada, junto al campo y
 * en español, en vez de volver como un fallo de restricción que nadie sabe leer.
 */

const TIPOS = ['cc', 'ce'] as const;
const ROLES = ['operario', 'jefe_operarios', 'contador', 'administrador'] as const;

const numeroNormalizado = z.string().transform((s) => s.trim().toUpperCase());

/** Patrón por tipo. La cédula de ciudadanía es numérica; la de extranjería admite hasta dos letras. */
function patronDe(tipo: (typeof TIPOS)[number]): RegExp {
  return tipo === 'cc' ? /^[0-9]{5,12}$/ : /^[A-Z]{0,2}[0-9]{5,12}$/;
}

function mensajeDocumento(tipo: (typeof TIPOS)[number]): string {
  return tipo === 'cc'
    ? 'La cédula de ciudadanía tiene entre 5 y 12 dígitos, sin letras, puntos ni espacios'
    : 'La cédula de extranjería admite hasta dos letras seguidas de entre 5 y 12 dígitos, como E1234567';
}

export const esquemaIngreso = z
  .object({
    tipoDocumento: z.enum(TIPOS, { required_error: 'Elegir el tipo de documento' }),
    numeroDocumento: numeroNormalizado,
    password: z.string().min(1, 'Escribir la contraseña'),
  })
  .superRefine((v, ctx) => {
    if (!patronDe(v.tipoDocumento).test(v.numeroDocumento)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['numeroDocumento'],
        message: mensajeDocumento(v.tipoDocumento),
      });
    }
  });

export type DatosIngreso = z.infer<typeof esquemaIngreso>;

/**
 * Diez caracteres con al menos una letra y un número.
 *
 * No se piden símbolos ni mayúsculas obligatorias: el operario la escribe en un celular
 * a las dos de la mañana, y una regla de complejidad barroca produce contraseñas
 * escritas en un papel pegado a la caja, que es peor que una contraseña simple.
 */
export const esquemaCambioPassword = z
  .object({
    password: z
      .string()
      .min(10, 'La contraseña necesita al menos 10 caracteres')
      .regex(/[A-Za-zÁÉÍÓÚáéíóúÑñ]/, 'La contraseña necesita al menos una letra')
      .regex(/[0-9]/, 'La contraseña necesita al menos un número'),
    confirmacion: z.string(),
  })
  .superRefine((v, ctx) => {
    if (v.password !== v.confirmacion) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['confirmacion'],
        message: 'Las dos contraseñas no coinciden',
      });
    }
  });

export type DatosCambioPassword = z.infer<typeof esquemaCambioPassword>;

export const esquemaCrearUsuario = z
  .object({
    tipoDocumento: z.enum(TIPOS, { required_error: 'Elegir el tipo de documento' }),
    numeroDocumento: numeroNormalizado,
    nombreCompleto: z
      .string()
      .transform((s) => s.trim())
      .refine((s) => s.length >= 3, 'Escribir el nombre completo'),
    rol: z.enum(ROLES, { required_error: 'Elegir el rol' }),
    sedeId: z.string().uuid('Elegir una sede').nullable(),
  })
  .superRefine((v, ctx) => {
    if (!patronDe(v.tipoDocumento).test(v.numeroDocumento)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['numeroDocumento'],
        message: mensajeDocumento(v.tipoDocumento),
      });
    }
    // usuario_sede_ck: el contador y el administrador operan sobre todas las sedes.
    const sinSede = v.rol === 'contador' || v.rol === 'administrador';
    if (!sinSede && v.sedeId === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sedeId'],
        message: 'El operario y el jefe de operarios trabajan en una sede: elegir cuál',
      });
    }
  });

export type DatosCrearUsuario = z.infer<typeof esquemaCrearUsuario>;
